import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import cap17drive from "../index";
import auth from "../../../utils/authentication";
import {getLastSeenEpisode} from "../../../utils/TorresowoBackend";
import {fetchTmdbSerieImages} from "../api/cap17drive-api";
import {SquadflixNavigationRecord} from "../squadflixHome/useSquadflixNavigate";
import {pickBestTmdbImages} from "./useMovieDetailsData";

export function isFirstSerieEpisode(lastEpisode: any) {
    return lastEpisode?.season_number === 1 && lastEpisode?.episode_number === 1;
}

export const useSerieDetailsData = (id: string) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [from] = useState(() => SquadflixNavigationRecord.getLastEntry());
    const serie = useSelector(cap17drive.selectors.getSelectedSerie);
    const seasonLoaded = useSelector(cap17drive.selectors.getSelectedSeasonNumber);
    const [season, setSeason] = useState<number | null>(null);
    const [lastEpisode, setLastEpisode] = useState<any>(null);
    const [logoImage, setLogoImage] = useState<any>(null);
    const [backgroundImage, setBackgroundImage] = useState<any>(null);

    useEffect(() => {
        if (!id || !auth.currentUser) return;
        dispatch(cap17drive.actions.clearSelectedSerie() as any);
        dispatch(cap17drive.actions.clearSelectedSeason() as any);
        dispatch(cap17drive.actions.clearSelectedEpisode() as any);
        dispatch(cap17drive.actions.fetchSerie(id, auth.currentUser.uid) as any);
        getLastSeenEpisode(auth.currentUser.uid, id).then(res => {
            if (res) {
                setSeason(res.season_number);
                setLastEpisode(res);
            }
        }).catch(() => {});
    }, [dispatch, id]);

    useEffect(() => {
        if (!season && seasonLoaded) {
            setSeason(seasonLoaded);
        }
    }, [season, seasonLoaded]);

    useEffect(() => {
        if (!id) return;
        setLogoImage(null);
        setBackgroundImage(null);
        fetchTmdbSerieImages(id).then(res => {
            const {selectedLogo, selectedBackdrop} = pickBestTmdbImages(res);
            setLogoImage(selectedLogo);
            setBackgroundImage(selectedBackdrop);
        }).catch(() => {});
    }, [id]);

    const backgroundAspectRatio = useMemo(() => {
        return backgroundImage ? (backgroundImage.height / backgroundImage.width) : (9 / 16);
    }, [backgroundImage]);

    const handleClickPlay = useCallback(() => {
        if (!lastEpisode || isFirstSerieEpisode(lastEpisode)) {
            navigate(`/squadflix/series/${id}/1/1`);
        } else {
            navigate(`/squadflix/series/${id}/${lastEpisode.season_number}/${lastEpisode.episode_number}`);
        }
    }, [id, lastEpisode, navigate]);

    const clearAndGoBack = useCallback(() => {
        dispatch(cap17drive.actions.clearSelectedEpisode() as any);
        dispatch(cap17drive.actions.clearSelectedSerie() as any);
        navigate(from.pathName || "/squadflix");
    }, [dispatch, from.pathName, navigate]);

    return {
        serie,
        season: season ?? seasonLoaded,
        setSeason,
        seasonLoaded,
        lastEpisode,
        isFirstEpisode: isFirstSerieEpisode(lastEpisode),
        logoImage,
        backgroundImage,
        backgroundAspectRatio,
        from,
        handleClickPlay,
        clearAndGoBack,
    };
};

export default useSerieDetailsData;
